import { DataHistory, DataModel } from '@shared/models/data-model';
import { Creatable, Deletable, Editable } from '@/types/model.types';
import { PlatformCreateUserGroup } from '@data/_generated-api/platform-api/models/platform-create-user-group';
import { PlatformUpdateUserGroup } from '@data/_generated-api/platform-api/models/platform-update-user-group';
import { baseFeatureId } from '@/app/constants';
import { Form } from '@common/formly/form.decorator';
import { userGroupEntityFormConfig } from '@/app/platform/platform-data/models/user-group.entity.form-config';
import { FeatureAuthorizationEntity } from '@/app/platform/platform-data/models/feature-authorization.entity';
import { NavigationItemEntity } from '@/app/platform/platform-data/models/navigation-item.entity';
import { UserAccountEntity } from '@/app/platform/platform-data/models/user-account.entity';

@Form('create', userGroupEntityFormConfig)
@Form('edit', userGroupEntityFormConfig)
export class UserGroupEntity
  extends DataModel
  implements Creatable<PlatformCreateUserGroup>, Editable<PlatformUpdateUserGroup>, Deletable<string>
{
  constructor(config?: Partial<UserGroupEntity> & Partial<DataHistory>) {
    super(config?.id, config);
    this.name = config?.name;
    this.authorizations = config?.authorizations;
    this.memberIds = config?.memberIds;
  }

  public name: string;
  public authorizations: FeatureAuthorizationEntity[];
  public memberIds: string[];

  get deleteKey(): string {
    return this.id;
  }

  get creationModel(): PlatformCreateUserGroup {
    return {
      name: this.name
    };
  }

  get editingModel(): PlatformUpdateUserGroup {
    return {
      id: this.id,
      name: this.name
    };
  }

  hasAccessTo(navigationItem: NavigationItemEntity, featureId?: string): boolean {
    const idToSearch = featureId ?? baseFeatureId;

    return !!this.authorizations
      .filter(a => a.navigationItemId === navigationItem.id)
      .find(a => a.featureId === idToSearch);
  }

  findBaseAuthorization({ id }: NavigationItemEntity): FeatureAuthorizationEntity {
    return this.authorizations.find(
      a => a.navigationItemId === id && a.featureId === baseFeatureId
    );
  }

  getAuthorizationsFor(i: NavigationItemEntity): FeatureAuthorizationEntity[] {
    return this.authorizations.filter(a => a.navigationItemId === i.id);
  }

  hasMember(user: UserAccountEntity): boolean {
    return this.memberIds.includes(user.id);
  }

  static createDefault(): UserGroupEntity {
    return new UserGroupEntity({
      id: '',
      name: '',
      memberIds: [],
      authorizations: []
    });
  }
}
